import { FC, RefObject } from 'react';
import { Transaction } from '../../../context/context';
import {
  BalanceTableContainer,
  ErrorMessage,
  NoMoreMessage,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableRow,
  TableRowHead,
} from './BalanceTableSection.styles';
import { Markdown } from '../../../../shared/markdown';
import { ButtonGroupContainer, LoadingSpinner, Row } from '../../../../shared/Layout.styles';
import { balanceConstants } from '../../../../utils/constants/balance';
import { format } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { Button } from '../../../../shared/formElements/button';
import ReCAPTCHA from "react-google-recaptcha";

export interface IBalanceTable {
  transactions: Transaction[] | null;
  balance: number | null;
  hasMore: boolean;
  getMore: () => void;
  loading: boolean;
  currentPage: number;
  totalPages?: number;
  totalTransactions?: number;
  pageSize: number;
  serverErrors: string;
}

export const BalanceTable: FC<IBalanceTable> = ({
                                                  transactions,
                                                  balance,
                                                  hasMore,
                                                  getMore,
                                                  loading,
                                                  currentPage,
                                                  totalPages,
                                                  totalTransactions,
                                                  pageSize,
                                                  serverErrors
                                                }) => {
  const { balanceTableHeading } = balanceConstants;

  const handleNumber = (number: number) =>
    number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

  const transactionCount = transactions?.length || 0;

  return (
    <BalanceTableContainer>
      <Row>
        <Markdown children={balanceTableHeading} />
        <Markdown
          children={`## $${balance ? handleNumber(balance) : '0.00'}`}
          className="balanceValue"
        />
      </Row>

      <Row>
        <TableData>
          {transactionCount > 0 ? (
            <>
              Showing {transactionCount} transaction
              {transactionCount !== 1 ? 's' : ''}
              {totalTransactions &&
                ` • Total: ${totalTransactions} transactions`}
            </>
          ) : (
            <>No transactions found</>
          )}
        </TableData>
      </Row>

      <Table>
        <TableBody>
          <TableRowHead>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Balance</TableHead>
            <TableHead>Amount</TableHead>
          </TableRowHead>

          {!isEmpty(transactions) ? (
            transactions?.map((item) => (
              <TableRow key={item.id}>
                <TableData>
                  {format(
                    new Date(item.occurred_at),
                    "dd-MM-yyyy hh:mmaaaaa'm'"
                  )}
                </TableData>
                <TableData>{item.reference || 'N/A'}</TableData>
                <TableData>
                  {item.running_balance_amount
                    ? `$ ${handleNumber(item.running_balance_amount)}`
                    : 'N/A'}
                </TableData>
                <TableData>
                  {`$ ${handleNumber(item.amount)}`}
                </TableData>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableData colSpan={4}>No transactions found.</TableData>
            </TableRow>
          )}

          {hasMore && (
            <TableRow>
              <TableData colSpan={4}>
                {serverErrors && <ErrorMessage>{serverErrors}</ErrorMessage>}
                <ButtonGroupContainer align="left">
                  <Button onClick={() => getMore()} disabled={loading}>
                    {loading ? <LoadingSpinner show /> : 'Load More'}
                  </Button>
                </ButtonGroupContainer>
              </TableData>
            </TableRow>
          )}

          {!hasMore && transactions && transactions.length > 0 && (
            <TableRow>
              <NoMoreMessage colSpan={4}>
                No more transactions available.
              </NoMoreMessage>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </BalanceTableContainer>
  );
};